import type { MatchFiltersType } from 'src/shared/features/users/schemas/match'
import type { Profession } from 'src/shared/features/users/schemas/profession'

export const paths = {
  root: {
    path: '/',
    getHref: () => '/',
  },
  blog: {
    path: null,
    getHref: () => null,
    list: {
      path: '/blog',
      getHref: () => '/blog',
    },
    post: {
      path: '/blog/:slug',
      getHref: (slug: string) => `/blog/${slug}`,
    },
    studio: {
      path: '/blog/studio',
      getHref: () => '/blog/studio',
    },
  },
  landing: {
    path: null,
    getHref: () => null,
    google: {
      path: '/google-ads',
      getHref: () => '/google-ads',
      closingFrustrations: {
        path: '/google/closing-frustrations',
        getHref: () => '/google/closing-frustrations',
      },
      financingAd: {
        path: '/google/financing-google-ad',
        getHref: () => '/google/financing-google-ad',
      },
      inventoryCompetition: {
        path: '/google/inventory-competition',
        getHref: () => '/google/inventory-competition',
      },
      postPurchaseRegretMatch: {
        path: '/google/post-purchase-regret-match',
        getHref: () => '/google/post-purchase-regret-match',
      },
      realEstateMatch: {
        path: '/google/real-estate-match',
        getHref: () => '/google/real-estate-match',
      },
    },
    meta: {
      path: '/meta-ads',
      getHref: () => '/meta-ads',
    },
    vsl: {
      path: '/vsl',
      getHref: () => '/vsl',
    },
    affordable: {
      path: '/affordable',
      getHref: () => '/affordable',
    },
    approved: {
      path: '/approved',
      getHref: () => '/approved',
    },
    findAnAgent: {
      path: '/find-an-agent',
      getHref: () => '/find-an-agent',
    },
    douglasElliman: {
      path: '/douglas-elliman-abrishami',
      getHref: () => '/douglas-elliman-abrishami',
    },
    reTeam: {
      taylorKaneGroup: {
        path: '/re-team/taylor-kane-group',
        getHref: () => '/re-team/taylor-kane-group',
      },
      jerrySkefos: {
        path: '/re-team/novaroots-homes',
        getHref: () => '/re-team/novaroots-homes',
      },
      lanierGroup: {
        path: '/re-team/lanier-group',
        getHref: () => '/re-team/lanier-group',
      },
      kaseRealestate: {
        path: '/re-team/kase-realestate',
        getHref: () => '/re-team/kase-realestate',
      },
      realtyOneGroup: {
        path: '/re-team/realty-one-group',
        getHref: () => '/re-team/realty-one-group',
      },
      theCelestePandhiTeam: {
        path: '/re-team/the-celeste-pandhi-team',
        getHref: () => '/re-team/the-celeste-pandhi-team',
      },
      sell: {
        path: '/re-team/sell/:teamSlug',
        getHref: (teamSlug: string) => `/re-team/sell/${encodeURIComponent(teamSlug)}`,
      },
      buy: {
        path: '/re-team/buy/:teamSlug',
        getHref: (teamSlug: string) => `/re-team/buy/${encodeURIComponent(teamSlug)}`,
      },
    },
  },

  home: {
    path: '/home',
    getHref: () => '/home',
  },

  search: {
    path: '/search',
    getHref: () => '/search',
  },

  auth: {
    path: null,
    getHref: () => null,
    onboarding: {
      path: '/onboarding',
      getHref: (redirectTo?: string | null | undefined) =>
        `/onboarding${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`,
    },
    login: {
      path: '/login',
      getHref: (redirectTo?: string | null | undefined) =>
        `/login${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`,
    },
    loginPhone: {
      path: '/login-phone',
      getHref: (redirectTo?: string | null | undefined) =>
        `/login-phone${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`,
    },
    signup: {
      path: '/signup',
      getHref: (redirectTo?: string | null | undefined) =>
        `/signup${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`,
    },
    mfaSetup: {
      path: '/mfa-setup',
      getHref: (redirectTo?: string | null | undefined) =>
        `/mfa-setup${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`,
    },
    oauthCallback: {
      path: '/auth/callback',
      getHref: (redirectTo?: string | null | undefined) =>
        `/auth/callback${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`,
    },
    oauthLogout: {
      path: '/logout',
      getHref: () => '/logout',
    },
    oauthMfaVerify: {
      path: '/auth/oauth-mfa',
      getHref: () => '/auth/oauth-mfa',
    },
  },

  enterprise: {
    path: '/enterprise',
    getHref: () => '/enterprise',
    login: {
      path: '/enterprise/login',
      getHref: (redirectTo?: string | null | undefined) =>
        `/enterprise/login${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`,
    },
    loginPhone: {
      path: '/enterprise/login-phone',
      getHref: (redirectTo?: string | null | undefined) =>
        `/enterprise/login-phone${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`,
    },
    onboarding: {
      path: '/enterprise/onboarding',
      getHref: () => '/enterprise/onboarding',
    },
    mfaSetup: {
      path: '/enterprise/mfa-setup',
      getHref: () => '/enterprise/mfa-setup',
    },
    dashboard: {
      path: '/enterprise/dashboard',
      getHref: () => '/enterprise/dashboard',
      overview: {
        path: '/enterprise/dashboard/overview',
        getHref: () => '/enterprise/dashboard/overview',
      },
      directory: {
        path: '/enterprise/dashboard/directory',
        getHref: () => '/enterprise/dashboard/directory',
        invite: {
          path: '/enterprise/dashboard/directory/invite',
          getHref: () => '/enterprise/dashboard/directory/invite',
        },
      },
      billing: {
        path: '/enterprise/dashboard/billing',
        getHref: () => '/enterprise/dashboard/billing',
      },
      activity: {
        path: '/enterprise/dashboard/activity',
        getHref: () => '/enterprise/dashboard/activity',
      },
    },
    solutions: {
      path: '/enterprise/solutions',
      getHref: () => '/enterprise/solutions',
      smallCompanies: {
        path: '/enterprise/solutions/small-companies',
        getHref: () => '/enterprise/solutions/small-companies',
      },
      midMarkets: {
        path: '/enterprise/solutions/mid-markets',
        getHref: () => '/enterprise/solutions/mid-markets',
      },
      enterprises: {
        path: '/enterprise/solutions/enterprises',
        getHref: () => '/enterprise/solutions/enterprises',
      },
    },
    switchToConrad: {
      path: '/enterprise/switch-to-conrad',
      getHref: () => '/enterprise/switch-to-conrad',
    },
    howItWorks: {
      path: '/enterprise/how-it-works',
      getHref: () => '/enterprise/how-it-works',
    },
    securityAndCompliance: {
      path: '/enterprise/security-and-compliance',
      getHref: () => '/enterprise/security-and-compliance',
    },
    aboutUs: {
      path: '/enterprise/about-us',
      getHref: () => '/enterprise/about-us',
    },
  },

  professionals: {
    path: '/professionals',
    getHref: () => '/professionals',
    index: {
      path: '/professionals',
      getHref: () => '/professionals',
    },
    overview: {
      path: '/professionals/overview',
      getHref: () => '/professionals/overview',
    },
    integrations: {
      path: '/professionals/integrations',
      getHref: () => '/professionals/integrations',
    },
    solutionOne: {
      path: '/professionals/solution-one',
      getHref: () => '/professionals/solution-one',
    },
    implementAi: {
      path: '/professionals/implement-ai',
      getHref: () => '/professionals/implement-ai',
    },
    growYourNetwork: {
      path: '/professionals/grow-your-network',
      getHref: () => '/professionals/grow-your-network',
    },
    convertMoreLeads: {
      path: '/professionals/convert-more-leads',
      getHref: () => '/professionals/convert-more-leads',
    },
    runYourBusiness: {
      path: '/professionals/run-your-business',
      getHref: () => '/professionals/run-your-business',
    },
    getMoreBusiness: {
      path: '/professionals/get-more-business',
      getHref: () => '/professionals/get-more-business',
    },
  },

  consumer: {
    path: null,
    getHref: () => null,
    auth: {
      path: null,
      getHref: () => null,
      login: {
        path: '/public/login',
        getHref: (redirectTo?: string | null | undefined) =>
          `/public/login${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`,
      },
      signup: {
        path: '/public/signup',
        getHref: (redirectTo?: string | null | undefined) =>
          `/public/signup${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`,
      },
      loginPhone: {
        path: '/public/login-phone',
        getHref: (redirectTo?: string | null | undefined) =>
          `/public/login-phone${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`,
      },
    },
  },

  client: {
    path: null,
    getHref: () => null,
    index: {
      path: '/client',
      getHref: () => '/client',
    },
    business: {
      path: '/client/business/:businessId',
      getHref: ({
        businessId,
        profession,
        openDialog,
      }: {
        businessId: number
        profession?: Profession
        openDialog?: boolean
      }) => {
        let href = `/client/business/${businessId}`

        const params = new URLSearchParams()

        if (profession) params.set('profession', profession)
        if (openDialog) params.set('openDialog', openDialog.toString())

        if (params.size > 0) href += `?${params.toString()}`

        return href
      },
    },
  },

  business: {
    path: '/business',
    getHref: () => '/business',
    pending: {
      path: '/business/pending',
      getHref: () => '/business/pending',
    },
    create: {
      basePath: '/business/create',
      path: '/business/create/:step',
      getHref: (step: string) => `/business/create/${step}`,
    },
  },

  notifications: {
    path: '/notifications',
    getHref: () => '/notifications',
  },

  feed: {
    path: '/feed',
    getHref: () => '/feed',
    post: {
      path: '/post/:postId',
      getHref: (postId: string) => `/post/${postId}`,
      create: {
        path: '/post/create',
        getHref: () => '/feed/post/create',
      },
    },
    comment: {
      path: '/comment/:commentId',
      getHref: (commentId: string) => `/comment/${commentId}`,
    },
  },

  referrals: {
    path: null,
    getHref: () => null,
    list: {
      path: '/business',
      getHref: () => '/business',
      received: {
        path: '/business/received',
        getHref: () => '/business/received',
      },
      shared: {
        path: '/business/shared',
        getHref: () => '/business/shared',
      },
      pending: {
        path: '/business/pending',
        getHref: () => '/business/pending',
      },
    },
    getMatched: {
      path: '/get-matched',
      getHref: () => '/get-matched',
      info: {
        path: '/get-matched/info',
        getHref: (profession: string) => `/get-matched/info?profession=${encodeURIComponent(profession)}`,
      },
      results: {
        path: '/get-matched/results',
        getHref: (filters?: MatchFiltersType) => {
          if (!filters) return '/get-matched/results'
          const validFilters = Object.entries(filters)
            .filter(([_, value]) => {
              if (Array.isArray(value)) return value.length > 0
              return value != null && value !== '' && value !== 'undefined'
            })
            .map(([key, value]) => `${key}=${encodeURIComponent(value as string)}`)
          return validFilters.length ? `/get-matched/results?${validFilters.join('&')}` : '/get-matched/results'
        },
      },
    },
    detail: {
      referralShare: {
        path: '/referrals/received/:referralShareId',
        getHref: (referralShareId: string) => `/referrals/received/${referralShareId}`,
      },
      referral: {
        path: '/referrals/shared/:referralId',
        getHref: (referralId: string) => `/referrals/shared/${referralId}`,
      },
    },
    send: {
      path: '/referrals/send',
      getHref: (receiverUsername: string) => `/referrals/send?receiverUsername=${encodeURIComponent(receiverUsername)}`,
    },
  },

  leads: {
    path: '/leads',
    getHref: () => '/leads',
    grades: {
      path: '/leads/grading/how-it-works',
      getHref: (reason?: string) => {
        const href = '/leads/grading/how-it-works'
        if (!reason) return href
        return `${href}?reason=${encodeURIComponent(reason)}`
      },
    },
    detail: {
      path: '/leads/:leadId',
      getHref: (leadId: string) => `/leads/${leadId}`,
    },
  },

  chats: {
    list: {
      path: '/chats',
      getHref: () => '/chats',
    },
    conversation: {
      path: '/chats/:channelId',
      getHref: (channelId: string) => `/chats/${channelId}`,
      referral: {
        path: '/chats/referral/:referralShareId',
        getHref: (referralShareId: string) => `/chats/referral/${referralShareId}`,
      },
      match: {
        path: '/chats/match/:matchId',
        getHref: (matchId: string) => `/chats/match/${matchId}`,
      },
      network: {
        path: '/chats/network/:networkId',
        getHref: (networkId: string) => `/chats/network/${networkId}`,
      },
      sneak: {
        path: '/chats/sneak/:networkId',
        getHref: (networkId: string) => `/chats/sneak/${networkId}`,
      },
    },
  },

  partnerMatch: {
    path: '/matches',
    getHref: () => '/matches',
    unlock: {
      path: '/matches/unlock',
      getHref: () => '/matches/unlock',
    },
    preferences: {
      path: '/matches/preferences',
      getHref: () => '/matches/preferences',
    },
  },

  recap: {
    path: '/recap',
    getHref: (date: string) => `/recap?date=${date}`,
  },

  network: {
    path: '/network',
    getHref: () => '/network',
  },

  users: {
    path: null,
    getHref: () => null,
    completeProfile: {
      path: '/complete-profile',
      getHref: (redirectTo?: string | null | undefined) =>
        `/complete-profile${redirectTo ? `?redirectTo=${encodeURIComponent(redirectTo)}` : ''}`,
    },
    profile: {
      path: '/:username',
      getHref: (username: string) => `/${username}`,
    },
    publicProfile: {
      path: '/:username/public',
      getHref: (username: string) => `/${username}/public`,
      listings: {
        path: '/:username/public/listings',
        getHref: (username: string) => `/${username}/public/listings`,
      },
      reviews: {
        path: '/:username/public/reviews',
        getHref: (username: string) => `/${username}/public/reviews`,
      },
      activity: {
        path: '/:username/public/activity',
        getHref: (username: string) => `/${username}/public/activity`,
      },
      singleListing: {
        path: '/:username/public/listings/:listingId',
        getHref: (username: string, listingId: string) =>
          `/${encodeURIComponent(username)}/public/listings/${encodeURIComponent(listingId)}`,
      },
      portfolio: {
        path: '/:username/public/portfolio',
        getHref: (username: string) => `/${encodeURIComponent(username)}/public/portfolio`,
        single: {
          path: '/:username/public/portfolio/:portfolioId',
          getHref: (username: string, portfolioId: string) =>
            `/${encodeURIComponent(username)}/public/portfolio/${encodeURIComponent(portfolioId)}`,
        },
      },
    },
    message: {
      path: '/:username/message',
      getHref: (username: string) => `/${username}/message`,
    },
    sendReferral: {
      path: '/send-referral/:username',
      getHref: (username: string) => `/send-referral/${username}`,
    },
    editProfile: {
      path: '/me/edit/profile',
      getHref: () => '/me/edit/profile',
    },
    editLicenses: {
      path: '/me/edit/licenses',
      getHref: () => '/me/edit/licenses',
    },
    points: {
      path: '/points',
      getHref: () => '/points',
    },
    unsubscribe: {
      path: '/unsubscribe/:hashedId',
      getHref: (hashedId: string) => `/unsubscribe/${hashedId}`,
    },
  },

  marketplace: {
    path: '/marketplace',
    getHref: () => '/marketplace',
    details: {
      path: '/marketplace/:profession/:city/:state',
      getHref: (profession: string, city: string, state: string) =>
        `/marketplace/${encodeURIComponent(profession)}/${encodeURIComponent(city)}/${encodeURIComponent(state)}`,
    },
    filters: {
      path: '/marketplace/filters',
      getHref: () => '/marketplace/filters',
    },
    instantMatch: {
      path: '/marketplace/instant-match',
      getHref: (startStep?: 'buy' | 'sell' | 'lease' | 'refinance' | 'mortgage') => {
        if (startStep) {
          return `/marketplace/instant-match?startStep=${startStep}`
        }
        return '/marketplace/instant-match'
      },
      profession: {
        path: '/marketplace/instant-match/:profession',
        getHref: (profession: string, startStep?: 'buy' | 'sell' | 'lease' | 'refinance' | 'mortgage') => {
          if (startStep) {
            return `/marketplace/instant-match/${encodeURIComponent(profession)}?startStep=${startStep}`
          }
          return `/marketplace/instant-match/${encodeURIComponent(profession)}`
        },
      },
      userId: {
        path: '/marketplace/instant-match/user/:userId',
        getHref: (userId: string, startStep?: 'buy' | 'sell' | 'lease' | 'refinance' | 'mortgage') => {
          if (startStep) {
            return `/marketplace/instant-match/user/${encodeURIComponent(userId)}?startStep=${startStep}`
          }
          return `/marketplace/instant-match/user/${encodeURIComponent(userId)}`
        },
      },
      mortgageCalculator: {
        path: '/marketplace/instant-match/mortgage-calculator',
        getHref: () => '/marketplace/instant-match/mortgage-calculator',
      },
    },
  },

  settings: {
    path: '/settings',
    getHref: () => '/settings',
    notifications: {
      path: '/settings/notifications',
      getHref: () => '/settings/notifications',
    },
    security: {
      path: '/settings/security',
      getHref: () => '/settings/security',
      mfa: {
        path: '/settings/security/mfa',
        getHref: () => '/settings/security/mfa',
      },
    },
    personalInfo: {
      path: '/settings/personal-info',
      getHref: () => '/settings/personal-info',
    },
    personalInfoFields: {
      path: '/settings/personal-info/:fieldType',
      getHref: (fieldType: string) => `/settings/personal-info/${fieldType}`,
    },
    verifyEmail: {
      path: '/settings/verify-email',
      getHref: () => '/settings/verify-email',
    },
    featurePreferences: {
      path: '/settings/feature-preferences',
      getHref: () => '/settings/feature-preferences',
    },
    subscriptions: {
      path: '/settings/subscriptions',
      getHref: () => '/settings/subscriptions',
    },
    profilePrivacy: {
      path: '/settings/profile-privacy',
      getHref: () => '/settings/profile-privacy',
    },
  },

  subscriptions: {
    path: '/subscriptions',
    getHref: () => '/subscriptions',
    success: {
      path: '/subscriptions/success',
      getHref: () => '/subscriptions/success',
    },
    sessionCanceled: {
      path: '/subscriptions/session-canceled',
      getHref: () => '/subscriptions/session-canceled',
    },
    lifetime: {
      path: '/subscriptions/lifetime',
      getHref: (email?: string) => `/subscriptions/lifetime${email ? `?email=${encodeURIComponent(email)}` : ''}`,
    },
  },

  assistant: {
    path: '/lynk',
    getHref: () => '/lynk',
    conversation: {
      path: '/lynk/:conversationId',
      getHref: (conversationId: string) => `/lynk/${conversationId}`,
    },
    history: {
      path: '/lynk/history',
      getHref: () => '/lynk/history',
    },
    customize: {
      path: '/lynk/customize',
      getHref: () => '/lynk/customize',
    },
  },

  support: {
    path: '/support',
    getHref: () => '/support',
  },

  invite: {
    path: '/invite',
    getHref: () => '/invite',
  },

  alert: {
    path: '/alert',
    getHref: () => '/alert',
  },

  downloadApp: {
    path: '/download-app',
    getHref: () => '/download-app',
  },

  privacyPolicy: {
    path: '/privacy-policy',
    getHref: () => '/privacy-policy',
  },

  termsOfService: {
    path: '/terms-of-service',
    getHref: () => '/terms-of-service',
  },

  error: {
    notFound: {
      path: '/404',
      getHref: () => '/404',
    },
  },

  deleteAccount: {
    path: '/delete-account',
    getHref: () => '/delete-account',
    web: {
      path: '/delete-account/web',
      getHref: () => '/delete-account/web',
    },
  },

  admin: {
    path: '/admin',
    getHref: () => '/admin',
  },
} as const

export type Paths = typeof paths
